import { can } from '../auth/permissions';

/**
 * The coach-mark tour's steps: `[{ key, selector, copy }]`, where `copy` is
 * the i18n base in the `onboarding` namespace (the popover reads
 * `${copy}.title` and `${copy}.body`).
 *
 * The owner variant is today's tour, unchanged. The guest variant is built
 * from the invitee's role:
 *
 *  - it starts on the workspace switcher, since the space they were invited
 *    to is the reason they are here;
 *  - a step whose action the role lacks is **removed**, not left for the
 *    tour's missing-target skip, so the counter is honest (x / 4 for a
 *    Viewer, x / 5 for everyone else). The skip stays as a safety net;
 *  - copy is chosen by permission, not by role name, so it follows the matrix.
 */

// Today's four, verbatim: the owner tour must not change.
const OWNER_STEPS = [
  { key: 'dashboard', selector: '[data-tour="tour-dashboard"]', copy: 'tour.dashboard' },
  { key: 'movements', selector: '[data-tour="tour-movements"]', copy: 'tour.movements' },
  { key: 'sourcefiles', selector: '[data-tour="tour-sourcefiles"]', copy: 'tour.sourcefiles' },
  // MI-299: chat lost its sidebar item — the floating assistant is the entry point.
  { key: 'assistant', selector: '[data-tour="tour-assistant"]', copy: 'tour.assistant' },
];

const SELECTORS = {
  workspace: '[data-tour="tour-workspace"]',
  dashboard: '[data-tour="tour-dashboard"]',
  movements: '[data-tour="tour-movements"]',
  sourcefiles: '[data-tour="tour-sourcefiles"]',
  assistant: '[data-tour="tour-assistant"]',
};

const guestStep = (key, variant) => ({
  key,
  selector: SELECTORS[key],
  copy: `tour.guest.${key}.${variant}`,
});

function movementsVariant(role) {
  if (can(role, 'movement.update.any')) return 'edit';
  if (can(role, 'movement.create')) return 'own';
  return 'readonly';
}

function guestSteps(role) {
  return [
    // Co-Owners also manage the space's members from the switcher.
    guestStep('workspace', can(role, 'member.read') ? 'members' : 'default'),
    // `grant.request` is the action only an Advisor holds: whoever has it is
    // there to advise, and the overview is where that starts.
    guestStep('dashboard', can(role, 'grant.request') ? 'advise' : 'default'),
    guestStep('movements', movementsVariant(role)),
    can(role, 'source_file.read')
      && guestStep('sourcefiles', can(role, 'statement.upload') ? 'upload' : 'review'),
    guestStep('assistant', 'default'),
  ].filter(Boolean);
}

export function tourStepsFor({ variant, role }) {
  return variant === 'guest' ? guestSteps(role) : OWNER_STEPS;
}
